import type { ReactNode } from 'react';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { ConfigProvider, message, notification } from 'antd';
import enUS from 'antd/locale/en_US';
import jaJP from 'antd/locale/ja_JP';
import { useTranslation } from 'react-i18next';
import { StyleProvider } from '@ant-design/cssinjs';
import { usePreferencesState } from '@/features/preferences';
import { useKeyboardHeight } from '@/features/shell/useKeyboardHeight';
import { buildAntdTheme } from './antdTheme';
import { typeRemTransformer } from './typeRem';
import { AppearanceContext } from './appearanceContext';
import {
  applyAppearance,
  readSystemSettings,
  resolveColorScheme,
  resolveReducedMotion,
  watchSystemSettings,
  type SystemSettings,
} from './appearance';

export { useAppearance } from './appearanceContext';

// Ant Design's font sizes become rem, so its text follows the root font size
// that the text-size preference sets (tokens.css); control sizes, paddings
// and radii stay in px, as in the kit.
const transformers = [typeRemTransformer(16)];

// Kit ToastStack: every toast is centred 72 px from the top.
message.config({ top: 72 });
notification.config({ placement: 'top', top: 72 });

// The kit never spaces two-character Japanese labels ("削除", not "削 除").
const buttonConfig = { autoInsertSpace: false };

/** Applies the preferences to <html> and gives Ant Design the matching theme. */
const AppearanceRoot = ({ children }: { children: ReactNode }) => {
  const { prefs } = usePreferencesState();
  const { t, i18n } = useTranslation();
  const [system, setSystem] = useState<SystemSettings>(readSystemSettings);
  // Bottom sheets (globals.css) stay above the on-screen keyboard.
  useKeyboardHeight();

  useEffect(
    () => watchSystemSettings(() => setSystem(readSystemSettings())),
    [],
  );

  const appearance = useMemo(
    () => ({
      textSize: prefs.text_size,
      theme: prefs.theme,
      width: prefs.width,
      reduceMotion: prefs.reduce_motion,
    }),
    [prefs.text_size, prefs.theme, prefs.width, prefs.reduce_motion],
  );

  useLayoutEffect(() => {
    applyAppearance(appearance, system);
  }, [appearance, system]);

  const english = i18n.language === 'en-US';
  const locale = useMemo(() => {
    const base = english ? enUS : jaJP;
    // ja_JP has no label for the dialog close button; use the dictionary's.
    return { ...base, global: { ...base.global, close: t('v2.common.close') } };
  }, [english, t]);

  const scheme = resolveColorScheme(appearance.theme, system);
  const reducedMotion = resolveReducedMotion(appearance, system);
  const theme = useMemo(
    () => buildAntdTheme(scheme, reducedMotion),
    [scheme, reducedMotion],
  );

  const resolved = useMemo(
    () => ({ system, scheme, reducedMotion }),
    [system, scheme, reducedMotion],
  );

  return (
    <AppearanceContext.Provider value={resolved}>
      <StyleProvider transformers={transformers}>
        <ConfigProvider
          theme={theme}
          // Ant Design's own labels (Close, OK, …). Always an object: going
          // from no locale to one rebuilds ConfigProvider's tree and remounts
          // the whole app.
          locale={locale}
          button={buttonConfig}
        >
          {children}
        </ConfigProvider>
      </StyleProvider>
    </AppearanceContext.Provider>
  );
};

export default AppearanceRoot;
